import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, ArrowRight, CheckCircle2 } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const OUTCOMES = [
  { value: "resolved_for_customer", label: "Resolve for customer" },
  { value: "resolved_for_provider", label: "Resolve for provider" },
  { value: "partial_settlement", label: "Partial settlement" },
  { value: "closed", label: "Close without action" },
];

export default function AdminDisputesPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(null);
  useDocumentTitle("Admin · Disputes");

  const isAdmin = (user?.roles || []).some((r) => r === "administrator" || r === "super_administrator");

  const load = async () => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const { data } = await api.get("/admin/disputes", { params });
      setItems(data || []);
    } catch (err) { toast.error("Only admins can view this page."); } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(); /* eslint-disable-next-line */ }, [isAdmin, status]);

  if (!isAdmin) {
    return (
      <DashboardShell>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center max-w-lg">
          <div className="font-display text-lg text-[#071b22] font-semibold">Admins only</div>
          <div className="text-sm text-[#252A34]/70 mt-1">Sign in with an admin account to resolve disputes.</div>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell>
      <div className="max-w-5xl">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Dispute queue</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Full CMS with SLAs comes in Phase 7 — this page is enough to keep the queue moving.</p>
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white" data-testid="admin-disputes-filter">
            <option value="">All</option>
            {["open", "awaiting_customer", "awaiting_provider", "under_review", "escalated", "partial_settlement", "resolved_for_customer", "resolved_for_provider", "closed"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <Shield className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">Queue is empty.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-disputes-list">
            {items.map((d) => (
              <div key={d.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-dispute-${d.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{d.kind.replace(/_/g, " ")} · {d.status.replace(/_/g, " ")}</div>
                    <div className="font-display font-semibold text-[#071b22] mt-1">{d.subject}</div>
                    <div className="text-xs text-[#252A34]/60 mt-1">Reserved <b>${(d.reserved_amount || 0).toFixed(2)}</b> · {d.created_at ? new Date(d.created_at).toLocaleDateString() : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Link to={`/dashboard/disputes/${d.id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium" data-testid={`admin-dispute-view-${d.id}`}>
                      View <ArrowRight className="h-3 w-3" />
                    </Link>
                    {!d.resolved_at && (
                      <button onClick={() => setResolving(d)} className="inline-flex items-center gap-1 rounded-full bg-[#165a71] text-white px-3 py-1.5 text-xs font-medium" data-testid={`admin-dispute-resolve-${d.id}`}>
                        Resolve <CheckCircle2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {resolving && <ResolveDialog dispute={resolving} onClose={() => { setResolving(null); load(); }} />}
    </DashboardShell>
  );
}

function ResolveDialog({ dispute, onClose }) {
  const [outcome, setOutcome] = useState("resolved_for_customer");
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState("");
  const [releasePayout, setReleasePayout] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is required."); return; }
    setSaving(true);
    try {
      await api.post(`/admin/disputes/${dispute.id}/resolve`, {
        outcome, reason, refund_amount: refund ? Number(refund) : null, release_payout: releasePayout,
      });
      toast.success("Dispute resolved");
      onClose();
    } catch (err) { toast.error(err?.response?.data?.error || "Could not resolve"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4" data-testid="admin-resolve-dialog">
      <div className="w-full max-w-md rounded-3xl bg-white p-6 border border-slate-200 shadow-2xl">
        <div className="font-display text-lg text-[#071b22] font-semibold">Resolve dispute</div>
        <div className="text-xs text-[#252A34]/70 mt-1 truncate">{dispute.subject}</div>
        <div className="mt-4">
          <label className="text-xs text-[#252A34]/70">Outcome</label>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="admin-resolve-outcome">
            {OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="mt-3">
          <label className="text-xs text-[#252A34]/70">Reason (shown to both parties)</label>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="admin-resolve-reason" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-[#252A34]/70">Refund amount (optional)</label>
          <input type="number" value={refund} onChange={(e) => setRefund(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid="admin-resolve-refund" />
        </div>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-[#252A34]/80">
          <input type="checkbox" checked={releasePayout} onChange={(e) => setReleasePayout(e.target.checked)} data-testid="admin-resolve-release" />
          Release provider's reserved payout back to pending
        </label>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium">Cancel</button>
          <button onClick={submit} disabled={saving} className="rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="admin-resolve-submit">
            {saving ? "Saving…" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
